import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api/cliente';
import type { EventoDetalle, EventoResumen } from '../../api/tipos';
import {
  EsqueletoModalEvento,
  EsqueletoVistaColaborador,
} from '../../componentes/Esqueletos';
import { Vacio } from '../../componentes/Estados';
import { ModalEvento } from './ModalEvento';
import { PanelConfirmados } from './PanelConfirmados';
import { TarjetaEvento } from './TarjetaEvento';

const REFRESCO_VOTACION_MS = 10_000;

export function VistaColaborador() {
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const eventos = useQuery({
    queryKey: ['eventos'],
    queryFn: () => api.get<EventoResumen[]>('/eventos'),
  });

  // El primer evento de la lista queda elegido solo, como en el diseño.
  useEffect(() => {
    if (seleccionado || !eventos.data?.length) return;
    setSeleccionado(eventos.data[0]?.id ?? null);
  }, [eventos.data, seleccionado]);

  const detalle = useQuery({
    queryKey: ['evento', seleccionado],
    queryFn: () => api.get<EventoDetalle>(`/eventos/${seleccionado!}`),
    enabled: Boolean(seleccionado),
    // Votación en vivo: mientras haya resultados visibles se refresca cada
    // REFRESCO_VOTACION_MS. Con la pestaña oculta TanStack Query no consulta.
    refetchInterval: (consulta) =>
      consulta.state.data?.resultados?.length ? REFRESCO_VOTACION_MS : false,
  });

  const detalleAbierto = useQuery({
    queryKey: ['evento', abierto],
    queryFn: () => api.get<EventoDetalle>(`/eventos/${abierto!}`),
    enabled: Boolean(abierto),
  });

  if (eventos.isPending) return <EsqueletoVistaColaborador />;

  const sinPendientes =
    eventos.data?.every((evento) => evento.miEstado !== 'SIN_RESPONDER') ?? false;
  const pendientes = eventos.data?.filter((e) => e.miEstado === 'SIN_RESPONDER').length ?? 0;

  if (!eventos.data || eventos.data.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-7 sm:px-8">
        <div className="tarjeta">
          <Vacio
            titulo="Todavía no hay próximos eventos."
            detalle="Cuando se publique uno nuevo te va a aparecer acá para confirmar asistencia."
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-start gap-5 px-4 pb-12 pt-6 sm:px-8 lg:grid-cols-[336px_minmax(0,1fr)]">
        <div className="order-2 min-w-0">
          <PanelConfirmados
            detalle={detalle.data}
            cargando={detalle.isPending}
            onResponder={() => seleccionado && setAbierto(seleccionado)}
          />
        </div>

        <div className="order-1 flex min-w-0 flex-col gap-2.5">
          <div className="mb-0.5 flex items-baseline justify-between gap-2.5">
            <h1 className="font-titulo text-[18px] font-extrabold">Próximos eventos</h1>
            <span className="text-right text-[11.5px] text-debil">
              {sinPendientes
                ? 'Respondiste todos'
                : `${pendientes} ${pendientes === 1 ? 'espera' : 'esperan'} tu respuesta`}
            </span>
          </div>

          {eventos.data.map((evento) => (
            <TarjetaEvento
              key={evento.id}
              evento={evento}
              seleccionado={evento.id === seleccionado}
              onSeleccionar={() => setSeleccionado(evento.id)}
            />
          ))}
        </div>
      </div>

      {abierto &&
        (detalleAbierto.data ? (
          <ModalEvento detalle={detalleAbierto.data} onCerrar={() => setAbierto(null)} />
        ) : (
          <div
            className="fixed inset-0 z-[60] overflow-y-auto bg-[rgba(8,15,26,.72)] px-4 py-8 sm:px-5 sm:py-12"
            onClick={() => setAbierto(null)}
            role="presentation"
          >
            <div className="mx-auto w-full max-w-[620px] overflow-hidden rounded-2xl border border-white/[.12] bg-panel shadow-modal">
              <div className="h-1 bg-white/10" />
              <EsqueletoModalEvento />
            </div>
          </div>
        ))}
    </>
  );
}
